import React from "react";
import { Heart, Quote } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Reusable "share your Guardian story" prompt shown right after a positive
 * outcome — a resolved SOS, a Lost & Found reunion, etc. Submissions go into
 * the existing moderated /api/testimonials queue.
 */
export default function StoryPromptDialog({
  open,
  onOpenChange,
  title = "Glad you're safe. Share what happened?",
  description = "A short story helps other families feel confident about Guardian360. If approved, it may appear on our homepage.",
  onSubmitted,
}) {
  const [story, setStory] = React.useState({ quote: "", city: "", stars: 5 });

  const submit = async () => {
    if (story.quote.trim().length < 20) return toast.error("Please write at least 20 characters");
    try {
      await api.post("/testimonials", story);
      toast.success("Thank you! Our team will review it shortly.");
      setStory({ quote: "", city: "", stars: 5 });
      onOpenChange(false);
      onSubmitted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-[hsl(var(--destructive))]" /> {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Your story (min. 20 chars)</Label>
            <Textarea data-testid="story-quote" rows={5} value={story.quote} onChange={(e) => setStory({ ...story, quote: e.target.value })} placeholder="Tonight, Guardian360…" />
          </div>
          <div>
            <Label>City (optional)</Label>
            <Input data-testid="story-city" value={story.city} onChange={(e) => setStory({ ...story, city: e.target.value })} placeholder="Mumbai" />
          </div>
          <div>
            <Label>How was your experience?</Label>
            <div className="flex gap-1 mt-1" data-testid="story-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setStory({ ...story, stars: n })}
                  className={`text-2xl ${n <= story.stars ? "text-[hsl(var(--accent))]" : "text-muted-foreground"}`}>★</button>
              ))}
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button data-testid="story-skip" variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
              Not now
            </Button>
            <Button data-testid="story-submit" onClick={submit} className="flex-1 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white">
              <Quote className="w-4 h-4 mr-2" /> Share my story
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
